'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface BannerSectionProps {
  className?: string;
}

const BannerSection = ({ className = '' }: BannerSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const bannerSlides = [
  {
    id: 1,
    title: "Your Global Education Journey Starts Here",
    subtitle: "Dream big, plan smart, succeed globally with Yunova International",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7420736-1766479018838.png",
    alt: "Diverse group of international students celebrating graduation with caps thrown in air",
    ctaPrimary: { text: "Start Your Journey", href: "/consultation-booking" },
    ctaSecondary: { text: "Explore Destinations", href: "/destinations" }
  },
  {
    id: 2,
    title: "Study Abroad with Expert Guidance",
    subtitle: "Navigate your international education journey with confidence",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13aadd5da-1771890722326.png",
    alt: "Student studying at modern university library with laptop and books",
    ctaPrimary: { text: "Book Consultation", href: "/consultation-booking" },
    ctaSecondary: { text: "Explore Destinations", href: "/destinations" }
  },
  {
    id: 3,
    title: "Transform Your Future Today",
    subtitle: "Join thousands of successful students who achieved their dreams",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_115e942f0-1772221221715.png",
    alt: "Happy international students walking together on university campus with modern buildings",
    ctaPrimary: { text: "Get Started", href: "/consultation-booking" },
    ctaSecondary: { text: "Learn More", href: "/blog" }
  }];


  const nextSlide = () => {
    if (!isHydrated) return;
    setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
  };

  const prevSlide = () => {
    if (!isHydrated) return;
    setCurrentSlide((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length);
  };

  useEffect(() => {
    if (!isHydrated) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [isHydrated, currentSlide]);

  if (!isHydrated) {
    return (
      <section className={`relative min-h-[600px] flex items-center justify-center bg-gradient-to-br from-primary to-secondary ${className}`}>
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Your Global Education Journey Starts Here</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">Dream big, plan smart, succeed globally with Yunova International</p>
        </div>
      </section>);

  }

  const currentBanner = bannerSlides[currentSlide];

  return (
    <section className={`relative min-h-[600px] flex items-center justify-center overflow-hidden ${className}`}>
      <div className="absolute inset-0">
        <AppImage
          src="/assets/images/rocket_gen_img_1a7420736-1766479018838-2-1767862357304.png"
          alt={currentBanner.alt}
          className="w-full h-full object-cover"
          priority />

        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70">
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-primary/80 hover:bg-primary rounded-full flex items-center justify-center backdrop-blur-sm transition-all duration-300"
        aria-label="Previous slide">
        <Icon name="ChevronLeftIcon" size={24} className="text-white" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-primary/80 hover:bg-primary rounded-full flex items-center justify-center backdrop-blur-sm transition-all duration-300"
        aria-label="Next slide">
        <Icon name="ChevronRightIcon" size={24} className="text-white" />
      </button>

      <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in">
          {currentBanner.title}
        </h1>
        <p className="text-lg md:text-xl lg:text-2xl mb-8 max-w-3xl mx-auto opacity-90">
          {currentBanner.subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            href={currentBanner.ctaPrimary.href}
            className="px-8 py-4 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
            {currentBanner.ctaPrimary.text}
          </Link>
          <Link
            href={currentBanner.ctaSecondary.href}
            className="px-8 py-4 bg-secondary text-secondary-foreground font-semibold rounded-lg hover:bg-secondary/90 backdrop-blur-sm transition-all duration-300">
            {currentBanner.ctaSecondary.text}
          </Link>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2 z-20">
        {bannerSlides.map((_, index) =>
        <button
          key={index}
          onClick={() => setCurrentSlide(index)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${
          index === currentSlide ? 'bg-primary w-8' : 'bg-white/50'}`
          }
          aria-label={`Go to slide ${index + 1}`} />
        )}
      </div>
    </section>);

};

export default BannerSection;